
import React, { useState } from "react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { PriorityBadge } from "@/components/notifications/StatusBadges";
import { cn } from "@/lib/utils";
import { useNotificationReadStatus } from "@/hooks/useNotificationReadStatus";
import NotificationStatusCell from "./list/NotificationStatusCell";
import NotificationActions from "./list/NotificationActions";
import { SupabaseNotification } from "@/types/notifications";
import { StockStatusBadge } from "./StockStatusBadge";
import { StockAlertDialog } from "./StockAlertDialog";
import { isAwaitingStockStatus } from "@/utils/statusMappings";

interface NotificationListItemProps {
  notification: SupabaseNotification;
  formatDate: (date: string) => string;
  onNotificationClick: (id: string) => void;
  onArchive: (e: React.MouseEvent, id: string) => void;
  onRestore: (e: React.MouseEvent, id: string) => void;
  onDelete: (e: React.MouseEvent, id: string) => void;
  onSendWebhook?: (e: React.MouseEvent, id: string) => void;
  isArchived: boolean;
  showBulkSelect?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
}

const NotificationListItem = React.memo(({
  notification,
  formatDate,
  onNotificationClick,
  onArchive,
  onRestore,
  onDelete,
  onSendWebhook,
  isArchived,
  showBulkSelect = false,
  isSelected = false,
  onToggleSelect
}: NotificationListItemProps) => {
  const { markAsRead, markAsUnread, isNotificationRead } = useNotificationReadStatus();
  const isRead = isNotificationRead(notification.id);
  const isHighPriority = notification.priority === 'hoog';
  const [showStockDialog, setShowStockDialog] = useState(false);
  
  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    markAsRead.mutate(notification.id);
  };

  const handleMarkAsUnread = (e: React.MouseEvent) => {
    e.stopPropagation();
    markAsUnread.mutate(notification.id);
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  
  return (
    <>
      <TableRow 
        key={notification.id} 
        className={cn(
          "cursor-pointer hover:bg-slate-50",
          !isRead && "bg-blue-50 font-medium",
          isHighPriority && "border-l-4 border-l-red-500",
          isSelected && "bg-blue-100 hover:bg-blue-100"
        )}
        onClick={() => onNotificationClick(notification.id)}
      >
        {showBulkSelect && (
          <TableCell onClick={handleCheckboxClick}>
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggleSelect?.(notification.id)}
              aria-label={`Selecteer melding ${notification.id}`}
            />
          </TableCell>
        )}
        <TableCell>
          <div className="relative flex items-center gap-2 min-w-[100px] h-6">
            {isAwaitingStockStatus(notification.status) ? (
              <StockStatusBadge 
                notificationId={notification.id}
                onClick={() => setShowStockDialog(true)}
              />
            ) : (
              <div className="w-4 h-4 opacity-0" />
            )}
          </div>
        </TableCell>
      <TableCell className="font-medium capitalize">{notification.type}</TableCell>
      <TableCell>{notification.vo_number || "-"}</TableCell>
      <TableCell className="max-w-xs truncate">{notification.message}</TableCell>
      <NotificationStatusCell notification={notification} />
      <TableCell><PriorityBadge priority={notification.priority} /></TableCell>
      <TableCell>{formatDate(notification.created_at)}</TableCell>
      <NotificationActions
        notificationId={notification.id}
        isRead={isRead}
        isArchived={isArchived}
        onMarkAsRead={handleMarkAsRead}
        onMarkAsUnread={handleMarkAsUnread}
        onView={onNotificationClick}
        onArchive={onArchive}
        onRestore={onRestore}
        onDelete={onDelete}
        onSendWebhook={onSendWebhook}
      />
    </TableRow>
    
    <StockAlertDialog
      notificationId={notification.id}
      voNumber={notification.vo_number}
      open={showStockDialog}
      onOpenChange={setShowStockDialog}
    />
    </>
  );
}, (prevProps, nextProps) => {
  // Optimized comparison: Early returns for performance
  if (prevProps.notification.id !== nextProps.notification.id) return false;
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.showBulkSelect !== nextProps.showBulkSelect) return false;
  
  // Use updated_at for efficient change detection
  if (prevProps.notification.updated_at !== nextProps.notification.updated_at) return false;
  
  // Return true to PREVENT re-render
  return true;
});

NotificationListItem.displayName = 'NotificationListItem';

export default NotificationListItem;
